import type {
  ApiError,
  ApiUpdateAuthorizationError,
  ApiUpdateAuthorizationState,
  ApiUpdateConnectionState,
  ApiUpdateCurrentUser,
  ApiUpdateServerTimeOffset,
  ApiUpdateSession,
  ApiUpdateShowAuthDialog,
} from '../../../api/types';
import type { RequiredGlobalActions } from '../../index';
import type { ActionReturnType, GlobalState } from '../../types';

import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { getShippingError, shouldClosePaymentModal } from '../../../util/getReadableErrorText';
import { unique } from '../../../util/iteratees';
import { setLanguage } from '../../../util/langProvider';
import { ACCOUNT_SLOT, getAccountsInfo, getAccountSlotUrl } from '../../../util/multiaccount';
import { clearWebTokenAuth } from '../../../util/routing';
import { setServerTimeOffset } from '../../../util/serverTime';
import { updateSessionUserId } from '../../../util/sessions';
import { forceWebsync } from '../../../util/websync';
import { isChatChannel, isChatSuperGroup } from '../../helpers';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import { updateUser, updateUserFullInfo } from '../../reducers';
import { updateTabState } from '../../reducers/tabs';
import { selectTabState } from '../../selectors';

addActionHandler('apiUpdate', (global, actions, update): ActionReturnType => {
  switch (update['@type']) {
    case 'updateApiReady':
      onUpdateApiReady(global);
      break;

    case 'updateAuthorizationState':
      onUpdateAuthorizationState(global, update);
      break;

    case 'updateAuthorizationError':
      onUpdateAuthorizationError(global, update);
      break;

    case 'updateWebAuthTokenFailed':
      onUpdateWebAuthTokenFailed(global);
      break;

    case 'updateConnectionState':
      onUpdateConnectionState(global, actions, update);
      break;

    case 'updateSession':
      onUpdateSession(global, actions, update);
      break;

    case 'updateServerTimeOffset':
      onUpdateServerTimeOffset(update);
      break;

    case 'updateCurrentUser':
      onUpdateCurrentUser(global, update);
      break;

    case 'requestReconnectApi':
      global = { ...global, isSynced: false };
      setGlobal(global);

      onUpdateConnectionState(global, actions, {
        '@type': 'updateConnectionState',
        connectionState: 'connectionStateConnecting',
      });
      actions.initApi();
      break;

    case 'requestSync':
      actions.sync();
      break;

    case 'updateFetchingDifference':
      global = { ...global, isFetchingDifference: update.isFetching };
      setGlobal(global);
      break;

    case 'showAuthDialog':
      onShowAuthDialog(global, update);
      break;

    case 'error': {
      const isDuringAuth = Boolean(
        global.authState && global.authState !== 'authorizationStateReady',
      );
      const { message, hasErrorKey } = update.error;

      if (isDuringAuth && message && !hasErrorKey) {
        onShowAuthDialog(global, { '@type': 'showAuthDialog', message });
        break;
      }

      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        const paymentShippingError = getShippingError(update.error);
        if (paymentShippingError) {
          actions.addPaymentError({ error: paymentShippingError, tabId });
        } else if (shouldClosePaymentModal(update.error)) {
          actions.closePaymentModal({ tabId });
        } else if (actions.showDialog) {
          actions.showDialog({ data: update.error, tabId });
        }
      });

      if (isDuringAuth) {
        global = getGlobal();
        global = {
          ...global,
          authIsLoading: false,
          authIsLoadingQrCode: false,
          authError: undefined,
        };
        setGlobal(global);
      }

      break;
    }
  }
});

function onUpdateApiReady<T extends GlobalState>(global: T) {
  void setLanguage(global.settings.byKey.language);
}

const PASSWORD_AUTH_ERRORS_TO_KEEP = new Set([
  'PASSWORD_HASH_INVALID',
  'USER_PASSWORD_INVALID',
  'USER_PASSWORD_ERROR',
  'USER_PASSWORD_INVALI',
  'PHONE_PASSWORD_FLOOD',
]);

function onUpdateAuthorizationState<T extends GlobalState>(global: T, update: ApiUpdateAuthorizationState) {
  global = getGlobal();

  const wasAuthReady = global.authState === 'authorizationStateReady';
  const authState = update.authorizationState;
  const authPasswordType = update.authPasswordType || 'twoStep';
  const isPasswordState = authState === 'authorizationStateWaitPassword';
  const authCodePurpose = authState === 'authorizationStateWaitCode'
    ? update.authCodePurpose
      || (
        global.authState === 'authorizationStateWaitPassword' && global.authPasswordType === 'login'
          ? 'passwordReset'
          : undefined
      )
    : undefined;
  const shouldClearPasswordError = authState === 'authorizationStateWaitPassword'
    && (
      global.authState !== 'authorizationStateWaitPassword'
      || global.authPasswordType !== authPasswordType
      || (
        global.authPasswordRequestId !== update.authPasswordRequestId
        && Boolean(global.authError)
        && !PASSWORD_AUTH_ERRORS_TO_KEEP.has(global.authError || '')
      )
    );

  global = {
    ...global,
    authState,
    authIsLoading: false,
    authError: shouldClearPasswordError ? undefined : global.authError,
    authPasswordType: isPasswordState
      ? authPasswordType
      : undefined,
    authPasswordRequestId: isPasswordState ? update.authPasswordRequestId : undefined,
    authCodePurpose,
  };
  setGlobal(global);

  global = getGlobal();

  switch (authState) {
    case 'authorizationStateLoggingOut':
      void forceWebsync(false);

      global = {
        ...global,
        isLoggingOut: true,
      };
      setGlobal(global);
      break;
    case 'authorizationStateWaitCode':
      global = {
        ...global,
        authIsCodeViaApp: update.isCodeViaApp,
        authCodePurpose,
      };
      setGlobal(global);
      break;
    case 'authorizationStateWaitPassword':
      global = {
        ...global,
        authHint: update.hint,
      };

      if (update.noReset) {
        global = {
          ...global,
          hasWebAuthTokenPasswordRequired: true,
        };
      }

      setGlobal(global);
      break;
    case 'authorizationStateWaitRegistration':
      global = {
        ...global,
        inviteCodeRequired: update.inviteCodeRequired,
      };
      setGlobal(global);
      break;
    case 'authorizationStateWaitQrCode':
      global = {
        ...global,
        authIsLoadingQrCode: false,
        authQrCode: update.qrCode,
      };
      setGlobal(global);
      break;
    case 'authorizationStateReady': {
      if (wasAuthReady) {
        break;
      }

      void forceWebsync(true);

      if (window.location.hash === '#login') {
        window.history.replaceState(undefined, '', getAccountSlotUrl(ACCOUNT_SLOT || 1, false));
      }

      global = {
        ...global,
        isLoggingOut: false,
      };
      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        global = updateTabState(global, {
          isInactive: false,
        }, tabId);
      });
      setGlobal(global);

      break;
    }
  }
}

function onShowAuthDialog<T extends GlobalState>(
  global: T,
  update: ApiUpdateShowAuthDialog,
) {
  const { message } = update;
  const data: ApiError = { message };

  Object.values(global.byTabId).forEach(({ id: tabId }) => {
    const newDialogs = [...selectTabState(global, tabId).dialogs];
    const existingErrorIndex = newDialogs.findIndex((err) => (
      'message' in err && (err as ApiError).message === message
    ));
    if (existingErrorIndex !== -1) {
      newDialogs.splice(existingErrorIndex, 1);
    }

    newDialogs.push(data);
    global = updateTabState(global, {
      dialogs: newDialogs,
    }, tabId);
  });

  global = {
    ...global,
    authIsLoading: false,
    authIsLoadingQrCode: false,
    authError: undefined,
  };
  setGlobal(global);
}

function onUpdateAuthorizationError<T extends GlobalState>(global: T, update: ApiUpdateAuthorizationError) {
  const alreadyAuthorized = update.message.match(/^USER_ALREADY_AUTHORIZED:(.+)$/);
  if (alreadyAuthorized) {
    const accounts = getAccountsInfo();
    const slot = Object.entries(accounts)
      .find(([, account]) => account.userId === alreadyAuthorized[1])?.[0];
    if (slot) {
      window.location.replace(getAccountSlotUrl(Number(slot), false, accounts[Number(slot)]?.isTest));
      return;
    }
  }

  if (update.message.includes('还未注册') || update.message.includes('sfw.vc')) {
    onShowAuthDialog(global, { '@type': 'showAuthDialog', message: update.message });
    return;
  }

  global = getGlobal();
  global = {
    ...global,
    authError: update.message,
    authIsLoading: false,
    authIsLoadingQrCode: false,
  };
  setGlobal(global);
}

function onUpdateWebAuthTokenFailed<T extends GlobalState>(global: T) {
  clearWebTokenAuth();
  global = getGlobal();

  global = {
    ...global,
    hasWebAuthTokenFailed: true,
  };
  setGlobal(global);
}

function onUpdateConnectionState<T extends GlobalState>(
  global: T, actions: RequiredGlobalActions, update: ApiUpdateConnectionState,
) {
  const { connectionState } = update;

  global = getGlobal();
  const tabState = selectTabState(global, getCurrentTabId());
  if (connectionState === 'connectionStateReady' && tabState.isMasterTab && tabState.multitabNextAction) {
    // @ts-ignore
    actions[tabState.multitabNextAction.action](tabState.multitabNextAction.payload);
    actions.clearMultitabNextAction({ tabId: tabState.id });
  }

  if (connectionState === global.connectionState) {
    return;
  }

  global = {
    ...global,
    connectionState,
  };
  setGlobal(global);

  if (global.isSynced) {
    const channelStackIds = Object.values(global.byTabId)
      .flatMap((tab) => tab.messageLists)
      .map((messageList) => messageList.chatId)
      .filter((chatId) => {
        const chat = global.chats.byId[chatId];
        return chat && (isChatChannel(chat) || isChatSuperGroup(chat));
      });
    if (connectionState === 'connectionStateReady' && channelStackIds.length) {
      unique(channelStackIds).forEach((chatId) => {
        actions.requestChannelDifference({ chatId });
      });
    }
  }

  if (connectionState === 'connectionStateBroken') {
    actions.signOut({ forceInitApi: true });
  }
}

function onUpdateSession<T extends GlobalState>(global: T, actions: RequiredGlobalActions, update: ApiUpdateSession) {
  const { sessionData } = update;
  global = getGlobal();
  const { authRememberMe, authState } = global;
  const isEmpty = !sessionData || !sessionData.mainDcId;

  if (!authRememberMe || authState !== 'authorizationStateReady' || isEmpty) {
    return;
  }

  actions.saveSession({ sessionData });
}

function onUpdateServerTimeOffset(update: ApiUpdateServerTimeOffset) {
  setServerTimeOffset(update.serverTimeOffset);
}

function onUpdateCurrentUser<T extends GlobalState>(global: T, update: ApiUpdateCurrentUser) {
  const { currentUser, currentUserFullInfo } = update;

  global = {
    ...updateUser(global, currentUser.id, currentUser),
    currentUserId: currentUser.id,
  };
  global = updateUserFullInfo(global, currentUser.id, currentUserFullInfo);
  setGlobal(global);

  updateSessionUserId(currentUser.id);
}
